import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { SipOrderLinkCreated, UserAccountDetail, VerificationUid } from './user-account-api.model';
import { environment } from '../../../environments/environment';
/** Backend-owned string values (bookingmgmt `VerificationType`, useraccount `UIDAuthenticationType`). */
const VERIFICATION_TYPE_SMS = 'SMS';
const UID_TYPE_SMS_VERIFICATION = 'SMS_VERIFICATION';

@Injectable({ providedIn: 'root' })
export class UserAccountApiService {
  private readonly http = inject(HttpClient);

  getById(userAccountId: number): Observable<UserAccountDetail> {
    return this.http.get<UserAccountDetail>(`${environment.userAccountUrl}/useraccounts/${userAccountId}`)
      .pipe(map(ua => ({ ...ua, id: Number(ua.id) })));
  }

  save(userAccount: UserAccountDetail): Observable<UserAccountDetail> {
    return this.http.put<UserAccountDetail>(`${environment.userAccountUrl}/useraccounts`, userAccount);
  }

  /**
   * Deactivate a user account via the dedicated action endpoint — the generic
   * save/PUT endpoint does not accept changes to the `status` field.
   */
  deactivate(userAccountId: number): Observable<void> {
    return this.http.post<void>(
      `${environment.userAccountUrl}/useraccounts/${userAccountId}/deactivate`,
      null,
    );
  }

  /**
   * Reactivate a user account via the dedicated action endpoint — the generic
   * save/PUT endpoint does not accept changes to the `status` field.
   */
  activate(userAccountId: number): Observable<void> {
    return this.http.post<void>(
      `${environment.userAccountUrl}/useraccounts/${userAccountId}/activate`,
      null,
    );
  }

  /**
   * Creates the SIP order link (or re-sends the open one) and emails it to the account's confirmed address.
   * useraccount takes the issuing employee from the Bearer token; AMIE never sees the token.
   *
   * @ticket PS-6586
   */
  sendSipOrderLink(userAccountId: number): Observable<SipOrderLinkCreated> {
    return this.http.post<SipOrderLinkCreated>(`${environment.userAccountUrl}/siporderlinks`, { userAccountId });
  }

  /**
   * EB-4453: send the SMS confirmation code for the account's mobile number.
   * bookingmgmt-api (`/bookingmgmt/v2`) — the legacy `/booking/v2` alias answers 503 on test.
   */
  sendVerificationCode(userAccountId: number): Observable<void> {
    return this.http.post<void>(
      `${environment.bookingMgmtUrl}/useraccounts/${userAccountId}/sendverificationcode`,
      null,
      { params: { verificationType: VERIFICATION_TYPE_SMS } },
    );
  }

  /**
   * EB-4453: fetch the code that was just sent. The agent does not type it in —
   * AMIE shows it and the agent compares it with what the caller reads out
   * (same as old CMT).
   */
  getConfirmationToken(userAccountId: number): Observable<VerificationUid> {
    return this.http.get<VerificationUid>(`${environment.userAccountUrl}/uids`, {
      params: { userAccountId: String(userAccountId), type: UID_TYPE_SMS_VERIFICATION },
    });
  }

  /** EB-4453: confirm the code; on `valid: true` the backend sets `mobileConfirmed`. */
  verifyCode(userAccountId: number, code: string): Observable<{ valid: boolean }> {
    return this.http.post<{ valid: boolean }>(`${environment.userAccountUrl}/uids/verify`, null, {
      params: { userAccountId: String(userAccountId), code },
    });
  }

  resetPassword(userAccountId: number): Observable<void> {
    return this.http.post<void>(`${environment.userAccountUrl}/accountrecovery/generatepassword`, null, {
      params: { userAccountId: String(userAccountId), sendVia: 'SMS' },
    });
  }

  getRendezvousKey(userAccountId: number): Observable<{ key?: string }> {
    return this.http.post<{ key?: string }>(`${environment.authUrl}/rendevouz`, { value: userAccountId });
  }

  /** Build OLD portal (MailClient) URL using rendezvous key */
  buildOldPortalUrl(rendezvousKey: string, employeeId: number): string {
    return `${environment.oldPortalUrl}mailclient/index.jsp?ccaid=${rendezvousKey}&mid=${employeeId}`;
  }

  /** Fetch NEW portal base URL from localization API v2, then build side-entrance URL */
  getNewPortalUrl(brandId: number): Observable<string | null> {
    const envKey = environment.portalEnvKey;
    return this.http.get<any>(`${environment.localizationV2Url}/i18n`, {
      params: {
        language: '1',
        brandId: String(brandId),
        fallback: 'false',
        globalKey: 'url',
        category: 'portal',
        localKey: envKey,
      },
    }).pipe(
      // v2 response: { url: { portal: { test: { translation: "https://..." } } } }
      map(res => res?.url?.portal?.[envKey]?.translation ?? null),
    );
  }
}
